import { useState } from "react";
import { ChevronDown, ChevronUp, LayoutGrid } from "lucide-react";
import { cn } from "~/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface BreakdownRow {
  label: string | null;
  count: number;
}

interface Breakdown {
  name: string;
  data: BreakdownRow[];
}

export interface CohortCardData {
  id: number;
  name: string;
  llm_description?: string | null;
  user_description?: string | null;
  // Number of participants matching the cohort's filters.
  participant_count?: number | null;
  // Total participants in the dataset (the denominator for "X of Y total").
  // This is dataset-level, not cohort-level.
  starting_participant_count?: number | null;
  pending_count?: boolean;
  // Demographic breakdown of the full dataset population (not the filtered cohort).
  // Used as reference context alongside the cohort count.
  starting_participant_breakdowns?: Breakdown[] | null;
  subject_label?: string | null;
}

interface CohortCardProps {
  cohort: CohortCardData;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString();
}

// ── Sub-components ────────────────────────────────────────────────────────────

function BreakdownGroup({ group, total }: { group: Breakdown; total: number }) {
  const max = Math.max(...group.data.map((r) => r.count), 1);
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {group.name}
      </p>
      <div className="space-y-1.5">
        {group.data.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-20 shrink-0 text-xs text-muted-foreground truncate">
              {row.label ?? "—"}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-primary/15 overflow-hidden">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${(row.count / max) * 100}%` }}
              />
            </div>
            <span className="w-12 shrink-0 text-right text-xs tabular-nums text-foreground">
              {fmt(row.count)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main card ─────────────────────────────────────────────────────────────────

export function CohortCard({ cohort }: CohortCardProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const desc = cohort.llm_description ?? cohort.user_description ?? null;
  const cohortCount = cohort.participant_count; // filtered cohort size
  const datasetTotal = cohort.starting_participant_count; // full dataset size (denominator)
  const pending = cohort.pending_count ?? false;
  const pct =
    cohortCount != null && datasetTotal != null && datasetTotal > 0
      ? Math.round((cohortCount / datasetTotal) * 100)
      : null;
  // Dataset-level demographic reference — not the cohort's own breakdowns.
  const datasetBreakdowns = cohort.starting_participant_breakdowns ?? [];
  const hasBreakdowns = datasetBreakdowns.length > 0;

  return (
    <div className="overflow-hidden rounded-xl border-2 border-primary/60 bg-card text-card-foreground shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2">
        <div className="min-w-0">
          <p className="font-bold text-base leading-tight truncate">{cohort.name}</p>
          {desc && <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{desc}</p>}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">ID {cohort.id}</span>
      </div>

      {/* Count + progress */}
      <div className="px-4 pb-3">
        {pending ? (
          <p className="text-sm italic text-muted-foreground">Calculating…</p>
        ) : cohortCount != null ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-primary">{fmt(cohortCount)}</span>
              {datasetTotal != null && (
                <span className="text-sm text-muted-foreground">
                  of {fmt(datasetTotal)} total
                  {pct != null && <span> · {pct}%</span>}
                </span>
              )}
            </div>
            {pct != null && (
              <div className="mt-2 h-1.5 w-full rounded-full bg-primary/15">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Breakdown toggle */}
      {hasBreakdowns && (
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={() => setShowBreakdown((v) => !v)}
            className={cn(
              "flex items-center gap-1 text-sm font-medium text-primary",
              "hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            )}
          >
            {showBreakdown ? (
              <ChevronUp className="size-4" aria-hidden />
            ) : (
              <ChevronDown className="size-4" aria-hidden />
            )}
            {showBreakdown ? "Hide" : "Show"} breakdown
          </button>

          {showBreakdown && (
            <div className="mt-3 rounded-lg bg-muted/50 px-3 py-3 space-y-4">
              {datasetBreakdowns.map((group, i) => (
                <BreakdownGroup key={i} group={group} total={datasetTotal ?? cohortCount ?? 1} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="border-t border-border px-4 py-3 space-y-2">
        <button
          type="button"
          disabled
          className="flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground opacity-50 cursor-not-allowed"
        >
          Export this cohort as a dataset →
        </button>
        <button
          type="button"
          disabled
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary px-4 py-2.5 text-sm font-semibold text-primary opacity-50 cursor-not-allowed"
        >
          <LayoutGrid className="size-4" aria-hidden />
          View filters
        </button>
      </div>
    </div>
  );
}
